/* AJBIR Atlas — a word from the artisan (sections/aj-artisan-note.liquid, assets/aj-artisan.css).
   The postcard slides in after the delay chosen in the section. It never takes the focus and never blocks the page.
   Closed with × (or Escape), or once its link is followed, it stays away for the number of days chosen; it shows at
   most once per visit. It waits while a drawer, the quick view or Shopify's cookie banner is open, and sits above
   Shopify's preview bar on a draft theme. In the theme editor it shows where it would show on the shop, and anywhere
   while its section is selected. */
(() => {
  const CLOSED = 'aj-artisan-closed';
  const SEEN = 'aj-artisan-seen';
  const read = (area, key) => { try { return window[area].getItem(key); } catch (e) { return null; } };
  const write = (area, key, value) => { try { window[area].setItem(key, value); } catch (e) { /* private browsing: it may show again */ } };
  const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const busy = () => {
    if (document.documentElement.classList.contains('aj-overflow-hidden')) return true;
    const banner = document.getElementById('shopify-pc__banner');
    return !!(banner && banner.getClientRects().length && getComputedStyle(banner).visibility !== 'hidden');
  };

  const init = (note) => {
    if (!note || note.dataset.ready) return;
    note.dataset.ready = '1';
    const design = note.hasAttribute('data-design');
    const days = Number(note.dataset.days) || 14;
    const delay = (Number(note.dataset.delay) || 0) * 1000;

    // Shopify's preview bar (draft themes) covers the bottom of the screen: the postcard sits above it
    const lift = () => {
      const bar = document.getElementById('preview-bar-iframe');
      let h = 0;
      if (bar) {
        const r = bar.getBoundingClientRect();
        if (r.height > 0 && r.top < window.innerHeight) h = Math.max(0, window.innerHeight - r.top);
      }
      note.style.setProperty('--an-preview', `${Math.round(h)}px`);
    };

    const show = async () => {
      if (!note.hidden) return;
      const img = note.querySelector('img');
      if (img) {
        img.loading = 'eager';
        await Promise.race([img.decode ? img.decode().catch(() => {}) : Promise.resolve(), wait(2500)]);
      }
      lift();
      note.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => note.classList.add('is-in')));
      if (!design) write('sessionStorage', SEEN, '1');
    };

    const hide = (remember) => {
      if (note.hidden) return;
      const hadFocus = note.contains(document.activeElement);
      note.classList.remove('is-in');
      if (remember && !design) write('localStorage', CLOSED, String(Date.now()));
      setTimeout(() => { note.hidden = true; }, reduced() ? 0 : 420);
      if (hadFocus) {
        const main = document.getElementById('PageContainer');
        if (main) main.focus({ preventScroll: true });
      }
    };

    note.addEventListener('click', (event) => {
      if (event.target.closest('[data-aj-artisan-close]')) hide(true);
      else if (event.target.closest('a[href]') && !design) write('localStorage', CLOSED, String(Date.now()));
    });
    note.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') hide(true);
    });

    if (design) {
      note.ajShow = show;
      note.ajHide = hide;
      if (note.hasAttribute('data-match')) show();
      return;
    }
    const closedAt = Number(read('localStorage', CLOSED)) || 0;
    if (Date.now() - closedAt < days * 864e5 || read('sessionStorage', SEEN)) return;
    const attempt = (tries) => {
      if (busy() && tries < 40) setTimeout(() => attempt(tries + 1), 1500);
      else show();
    };
    setTimeout(() => attempt(0), delay);
  };

  const scan = (root) => root.querySelectorAll('[data-aj-artisan]').forEach(init);
  scan(document);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:select', (event) => {
    const note = event.target.querySelector('[data-aj-artisan]');
    if (note && note.ajShow) note.ajShow();
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    const note = event.target.querySelector('[data-aj-artisan]');
    if (note && note.ajHide && !note.hasAttribute('data-match')) note.ajHide(false);
  });
})();
